import React, { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { createColumnHelper } from "@tanstack/react-table";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAffiliates } from "@/hooks/use-affiliates";
import { api } from "@/lib/api";
import { DataTable } from "@/components/DataTable";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { SearchInput } from "@/components/SearchInput";
import { EmptyState } from "@/components/EmptyState";
import { formatDate } from "@/lib/utils";
import { Users, CheckSquare, X } from "lucide-react";

interface AffiliateRow {
  id: number;
  name: string;
  status: string;
  defaultCurrencyId: string;
  hasNotifications: boolean;
  paymentType: string;
  createdAt: string;
}

const columnHelper = createColumnHelper<AffiliateRow>();

const columns = [
  columnHelper.accessor("id", {
    header: "ID",
    cell: (info) => (
      <span className="text-xs font-mono text-gray-500">#{info.getValue()}</span>
    ),
    size: 60,
  }),
  columnHelper.accessor("name", {
    header: "Name",
    cell: (info) => (
      <span className="font-medium text-gray-900">{info.getValue()}</span>
    ),
  }),
  columnHelper.accessor("status", {
    header: "Status",
    cell: (info) => <StatusBadge status={info.getValue()} />,
    size: 100,
  }),
  columnHelper.accessor("defaultCurrencyId", {
    header: "Currency",
    cell: (info) => (
      <span className="text-gray-600">{info.getValue()}</span>
    ),
    size: 80,
  }),
  columnHelper.accessor("paymentType", {
    header: "Payment",
    cell: (info) => (
      <span className="text-gray-600 capitalize">{info.getValue() || "None"}</span>
    ),
    size: 100,
  }),
  columnHelper.accessor("hasNotifications", {
    header: "Notifications",
    cell: (info) => (
      <span className={`text-xs font-medium ${info.getValue() ? "text-green-600" : "text-gray-400"}`}>
        {info.getValue() ? "On" : "Off"}
      </span>
    ),
    size: 100,
  }),
  columnHelper.accessor("createdAt", {
    header: "Created",
    cell: (info) => (
      <span className="text-gray-500 text-xs">{formatDate(info.getValue())}</span>
    ),
    size: 120,
  }),
];

export function AffiliatesPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [bulkStatus, setBulkStatus] = useState("");

  const { data, isLoading } = useAffiliates({ page, search, status: statusFilter || undefined });
  const affiliates = data?.data ?? [];
  const paging = data?.paging ?? { page: 1, pageSize: 50, totalCount: 0 };

  const bulkMutation = useMutation({
    mutationFn: (payload: { ids: number[]; updates: Record<string, unknown> }) =>
      api.patch("/v1/affiliates/bulk", payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["affiliates"] });
      setSelectedIds(new Set());
      setBulkStatus("");
    },
  });

  function toggleSelect(id: number) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleAll() {
    if (selectedIds.size === affiliates.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(affiliates.map((a: AffiliateRow) => a.id)));
    }
  }

  return (
    <div>
      <PageHeader
        title="Affiliates"
        description="Manage your affiliate partners"
        actionLabel="Add Affiliate"
        actionHref="/affiliates/new"
      />

      <div className="flex items-center gap-3 mb-4">
        <SearchInput
          value={search}
          onChange={(v) => { setSearch(v); setPage(1); }}
          placeholder="Search affiliates..."
          className="w-72"
        />
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-900"
        >
          <option value="">All Statuses</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="pending">Pending</option>
          <option value="rejected">Rejected</option>
        </select>
      </div>

      {/* Bulk Action Bar */}
      {selectedIds.size > 0 && (
        <div className="flex items-center gap-3 mb-4 bg-gray-900 text-white rounded-xl px-4 py-3">
          <CheckSquare className="w-4 h-4" />
          <span className="text-sm font-medium">{selectedIds.size} selected</span>
          <div className="flex items-center gap-2 ml-4">
            <select
              value={bulkStatus}
              onChange={(e) => setBulkStatus(e.target.value)}
              className="rounded-lg border border-gray-600 bg-gray-800 px-3 py-1.5 text-xs text-white focus:outline-none focus:ring-2 focus:ring-white"
            >
              <option value="">Change status...</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="pending">Pending</option>
            </select>
            <button
              onClick={() => {
                if (!bulkStatus) return;
                bulkMutation.mutate({ ids: Array.from(selectedIds), updates: { status: bulkStatus } });
              }}
              disabled={!bulkStatus || bulkMutation.isPending}
              className="px-3 py-1.5 text-xs font-medium bg-white text-gray-900 rounded-lg hover:bg-gray-100 disabled:opacity-50"
            >
              {bulkMutation.isPending ? "Applying..." : "Apply"}
            </button>
          </div>
          <button
            onClick={() => setSelectedIds(new Set())}
            className="ml-auto text-gray-400 hover:text-white p-1"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {!isLoading && affiliates.length === 0 && !search && !statusFilter ? (
        <EmptyState
          icon={<Users className="w-6 h-6 text-gray-400" />}
          title="No affiliates yet"
          description="Add your first affiliate partner to get started."
          actionLabel="Add Affiliate"
          actionHref="/affiliates/new"
        />
      ) : (
        <>
          {affiliates.length > 0 && (
            <div className="flex items-center gap-2 mb-2">
              <label className="flex items-center gap-2 text-xs text-gray-500 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedIds.size === affiliates.length && affiliates.length > 0}
                  onChange={toggleAll}
                  className="rounded border-gray-300 text-gray-900 focus:ring-gray-900"
                />
                Select all on page
              </label>
            </div>
          )}
          <DataTable
            columns={[
              {
                id: "select",
                header: "",
                cell: ({ row }: { row: { original: AffiliateRow } }) => (
                  <input
                    type="checkbox"
                    checked={selectedIds.has(row.original.id)}
                    onChange={(e) => { e.stopPropagation(); toggleSelect(row.original.id); }}
                    onClick={(e) => e.stopPropagation()}
                    className="rounded border-gray-300 text-gray-900 focus:ring-gray-900"
                  />
                ),
                size: 40,
              } as any,
              ...columns,
            ]}
            data={affiliates}
            page={paging.page}
            pageSize={paging.pageSize}
            totalCount={paging.totalCount}
            onPageChange={setPage}
            onRowClick={(row: AffiliateRow) => navigate({ to: "/affiliates/$id", params: { id: String(row.id) } })}
            isLoading={isLoading}
          />
        </>
      )}
    </div>
  );
}
